export type VideoType =
  | "youtube"
  | "vimeo"
  | "ted"
  | "imdb"
  | "dailymotion"
  | "wistia"
  | "vidyard"
  | "twitch"
  | "facebook"
  | "twitter"
  | "streamable"
  | "cloudflare"
  | "direct"
  | "unknown";

export interface VideoInfo {
  type: VideoType;
  id: string | null;
  embedUrl: string | null;
}

export interface VideoDetectionOptions {
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
}

/**
 * Detects video type from URL and generates appropriate embed URL
 */
export function detectVideoType(
  videoUrl: string | null | undefined,
  options: VideoDetectionOptions = {}
): VideoInfo {
  const {
    autoplay = true,
    muted = true,
    loop = true,
    controls = false,
  } = options;

  if (!videoUrl) {
    return { type: "unknown", id: null, embedUrl: null };
  }

  // YouTube detection
  const youtubeRegex =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const youtubeMatch = videoUrl.match(youtubeRegex);

  if (youtubeMatch) {
    const videoId = youtubeMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      mute: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      controls: controls ? "1" : "0",
      modestbranding: "1",
      rel: "0",
      showinfo: "0",
      iv_load_policy: "3",
      ...(loop && { playlist: videoId }), // Required for loop to work
    });

    return {
      type: "youtube",
      id: videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}?${params.toString()}`,
    };
  }

  // Vimeo detection
  const vimeoRegex = /(?:vimeo\.com\/)(?:.*\/)?(\d+)/;
  const vimeoMatch = videoUrl.match(vimeoRegex);

  if (vimeoMatch) {
    const videoId = vimeoMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      muted: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      controls: controls ? "1" : "0",
      title: "0",
      byline: "0",
      portrait: "0",
    });

    return {
      type: "vimeo",
      id: videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}?${params.toString()}`,
    };
  }

  // Dailymotion detection
  // Supports: https://www.dailymotion.com/video/x8abcde or https://dai.ly/x8abcde
  const dailymotionRegex =
    /(?:dailymotion\.com\/video|dai\.ly)\/([a-zA-Z0-9]+)/;
  const dailymotionMatch = videoUrl.match(dailymotionRegex);

  if (dailymotionMatch) {
    const videoId = dailymotionMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      mute: muted ? "1" : "0",
      loop: loop ? "1" : "0",
      controls: controls ? "1" : "0",
      "queue-enable": "0",
      "ui-highlight": "000000",
    });

    return {
      type: "dailymotion",
      id: videoId,
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}?${params.toString()}`,
    };
  }

  // Wistia detection
  // Supports: https://company.wistia.com/medias/abcd1234 or wistia embed codes
  const wistiaRegex = /(?:wistia\.com\/medias|wi\.st)\/([a-zA-Z0-9]+)/;
  const wistiaMatch = videoUrl.match(wistiaRegex);

  if (wistiaMatch) {
    const videoId = wistiaMatch[1];
    const params = new URLSearchParams({
      autoPlay: autoplay ? "true" : "false",
      muted: muted ? "true" : "false",
      playbar: controls ? "true" : "false",
      controlsVisibleOnLoad: controls ? "true" : "false",
      fullscreenButton: controls ? "true" : "false",
      endVideoBehavior: loop ? "loop" : "default",
    });

    return {
      type: "wistia",
      id: videoId,
      embedUrl: `https://fast.wistia.net/embed/iframe/${videoId}?${params.toString()}`,
    };
  }

  // Vidyard detection
  // Supports: https://video.vidyard.com/watch/abc123 or share links
  const vidyardRegex = /vidyard\.com\/(?:watch|share)\/([a-zA-Z0-9]+)/;
  const vidyardMatch = videoUrl.match(vidyardRegex);

  if (vidyardMatch) {
    const videoId = vidyardMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      loop: loop ? "1" : "0",
    });

    return {
      type: "vidyard",
      id: videoId,
      embedUrl: `https://play.vidyard.com/${videoId}?${params.toString()}`,
    };
  }

  // Twitch detection (Clips and VODs)
  // Clips: https://clips.twitch.tv/ClipSlug or https://www.twitch.tv/channel/clip/ClipSlug
  // VODs: https://www.twitch.tv/videos/123456789
  const twitchClipRegex = /clips\.twitch\.tv\/([a-zA-Z0-9_-]+)/;
  const twitchClipMatch = videoUrl.match(twitchClipRegex);

  if (twitchClipMatch) {
    const clipSlug = twitchClipMatch[1];
    const params = new URLSearchParams({
      clip: clipSlug,
      parent: window.location.hostname,
      autoplay: autoplay ? "true" : "false",
      muted: muted ? "true" : "false",
    });

    return {
      type: "twitch",
      id: clipSlug,
      embedUrl: `https://clips.twitch.tv/embed?${params.toString()}`,
    };
  }

  const twitchChannelRegex = /www\.twitch\.tv\/([a-zA-Z0-9_-]+)/;
  const twitchChannelMatch = videoUrl.match(twitchChannelRegex);

  if (twitchChannelMatch) {
    const clipSlug = twitchChannelMatch[1];
    const params = new URLSearchParams({
      channel: clipSlug,
      parent: window.location.hostname,
      autoplay: autoplay ? "true" : "false",
      muted: muted ? "true" : "false",
    });

    return {
      type: "twitch",
      id: clipSlug,
      embedUrl: `https://player.twitch.tv/?${params.toString()}`,
    };
  }

  const twitchVodRegex = /www\.twitch\.tv\/videos\/(\d+)/;
  const twitchVodMatch = videoUrl.match(twitchVodRegex);

  if (twitchVodMatch) {
    const videoId = twitchVodMatch[1];
    const params = new URLSearchParams({
      video: videoId,
      parent: window.location.hostname,
      autoplay: autoplay ? "true" : "false",
      muted: muted ? "true" : "false",
    });

    return {
      type: "twitch",
      id: videoId,
      embedUrl: `https://player.twitch.tv/?${params.toString()}`,
    };
  }

  // Facebook detection
  // Supports: https://www.facebook.com/username/videos/123456789/
  const facebookRegex = /facebook\.com\/.*\/videos\/(\d+)/;
  const facebookMatch = videoUrl.match(facebookRegex);

  if (facebookMatch) {
    const videoId = facebookMatch[1];
    const encodedUrl = encodeURIComponent(videoUrl);
    const params = new URLSearchParams({
      href: encodedUrl,
      autoplay: autoplay ? "true" : "false",
      muted: muted ? "1" : "0",
    });

    return {
      type: "facebook",
      id: videoId,
      embedUrl: `https://www.facebook.com/plugins/video.php?${params.toString()}`,
    };
  }

  // Twitter/X detection
  // Supports: https://twitter.com/user/status/123456789 or x.com
  const twitterRegex = /(?:twitter\.com|x\.com)\/\w+\/status\/(\d+)/;
  const twitterMatch = videoUrl.match(twitterRegex);

  if (twitterMatch) {
    const tweetId = twitterMatch[1];
    // Twitter embeds need the full URL
    return {
      type: "twitter",
      id: tweetId,
      embedUrl: `https://platform.twitter.com/embed/Tweet.html?id=${tweetId}`,
    };
  }

  // Streamable detection
  // Supports: https://streamable.com/abc123
  const streamableRegex = /streamable\.com\/([a-zA-Z0-9]+)/;
  const streamableMatch = videoUrl.match(streamableRegex);

  if (streamableMatch) {
    const videoId = streamableMatch[1];
    return {
      type: "streamable",
      id: videoId,
      embedUrl: `https://streamable.com/e/${videoId}?autoplay=${
        autoplay ? "1" : "0"
      }&muted=${muted ? "1" : "0"}`,
    };
  }

  // Cloudflare Stream detection
  // Supports: https://watch.cloudflarestream.com/abc123 or customer domains with video ID
  const cloudflareRegex =
    /(?:cloudflarestream\.com|watch\.cloudflarestream\.com)\/([a-zA-Z0-9]+)/;
  const cloudflareMatch = videoUrl.match(cloudflareRegex);

  if (cloudflareMatch) {
    const videoId = cloudflareMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "true" : "false",
      muted: muted ? "true" : "false",
      loop: loop ? "true" : "false",
      controls: controls ? "true" : "false",
    });

    return {
      type: "cloudflare",
      id: videoId,
      embedUrl: `https://iframe.cloudflarestream.com/${videoId}?${params.toString()}`,
    };
  }

  // TED detection
  // Supports formats like:
  // https://www.ted.com/talks/speaker_name_talk_title
  // https://embed.ted.com/talks/speaker_name_talk_title
  const tedRegex = /(?:ted\.com\/talks\/)([a-zA-Z0-9_-]+)/;
  const tedMatch = videoUrl.match(tedRegex);

  if (tedMatch) {
    const talkId = tedMatch[1];
    // TED embed doesn't support autoplay parameter reliably
    return {
      type: "ted",
      id: talkId,
      embedUrl: `https://embed.ted.com/talks/${talkId}`,
    };
  }

  // IMDb detection
  // Supports formats like:
  // https://www.imdb.com/video/vi123456789
  // https://www.imdb.com/title/tt1234567/videoplayer/vi123456789
  const imdbRegex = /imdb\.com\/(?:video|title\/[^\/]+\/videoplayer)\/(vi\d+)/;
  const imdbMatch = videoUrl.match(imdbRegex);

  if (imdbMatch) {
    const videoId = imdbMatch[1];
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      muted: muted ? "1" : "0",
    });

    return {
      type: "imdb",
      id: videoId,
      embedUrl: `https://www.imdb.com/video/imdb/${videoId}/imdb/embed?${params.toString()}`,
    };
  }

  // Direct video file detection
  const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/i;
  if (videoExtensions.test(videoUrl)) {
    return {
      type: "direct",
      id: null,
      embedUrl: videoUrl,
    };
  }

  return { type: "unknown", id: null, embedUrl: null };
}

/**
 * Get a human-readable list of supported video platforms
 */
export function getSupportedPlatforms(): string {
  return "YouTube, Vimeo, Dailymotion, Wistia, Vidyard, Twitch, Facebook, Twitter/X, Streamable, Cloudflare Stream, TED, IMDb, MP4, WebM, OGG";
}

/**
 * Check if a video type supports reliable autoplay
 */
export function supportsReliableAutoplay(videoType: VideoType): boolean {
  // These platforms have good autoplay support
  const reliableAutoplayPlatforms: VideoType[] = [
    "youtube",
    "vimeo",
    "dailymotion",
    "wistia",
    "vidyard",
    "streamable",
    "cloudflare",
    "direct",
  ];

  return reliableAutoplayPlatforms.includes(videoType);
}

/**
 * Check if a video type might need programmatic play attempts
 */
export function needsProgrammaticPlay(videoType: VideoType): boolean {
  const programmaticPlayPlatforms: VideoType[] = [
    "ted",
    "imdb",
    "twitch",
    "facebook",
    "twitter",
  ];

  return programmaticPlayPlatforms.includes(videoType);
}
